import axios from "axios";
import { toast } from "sonner";
import { getDeviceId } from "./deviceId";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const http = axios.create({ baseURL: API });

// Attach JWT token from localStorage on every request
http.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("auth_token");
    if (t) config.headers.Authorization = `Bearer ${t}`;
    // Device-Trust identifier (Feb 2026). Every request carries the
    // per-browser UUID so the backend can enforce trusted-device
    // policy on auth AND revoke tokens whose device was un-trusted.
    config.headers["X-Device-Id"] = getDeviceId();
    // Manager-override token (5-min temporary elevation). The header is
    // only sent if a non-expired override is in storage; AuthContext
    // clears it automatically when the countdown reaches zero.
    const ov = localStorage.getItem("override_token");
    const ovExp = localStorage.getItem("override_expires_at");
    if (ov && ovExp && new Date(ovExp).getTime() > Date.now()) {
      config.headers["X-Override-Token"] = ov;
    }
  } catch {
    /* ignore */
  }
  return config;
});

// On 401, clear stale token (let UI redirect to /login)
http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
      } catch {
        /* ignore */
      }
    }
    // Normalise FastAPI/Pydantic 422 errors so callers that pass
    // ``e.response.data.detail`` straight into a toast don't accidentally
    // hand React an array of validation-error objects (which crashes the
    // render with "Objects are not valid as a React child"). We replace
    // the array with a human-readable string AND keep the original under
    // ``detail_raw`` for advanced consumers.
    try {
      const d = err?.response?.data;
      if (d && Array.isArray(d.detail)) {
        const msg = d.detail
          .map((e) => {
            if (e && typeof e === "object") {
              const loc = Array.isArray(e.loc) ? e.loc.join(".") : e.loc;
              return e.msg ? `${loc ? loc + ": " : ""}${e.msg}` : JSON.stringify(e);
            }
            return String(e);
          })
          .join(" · ");
        d.detail_raw = d.detail;
        d.detail = msg || "Σφάλμα επικύρωσης";
      } else if (d && d.detail && typeof d.detail === "object") {
        d.detail_raw = d.detail;
        d.detail = d.detail.msg || d.detail.message || JSON.stringify(d.detail);
      }
    } catch {
      /* keep the original error if normalisation itself blows up */
    }
    return Promise.reject(err);
  }
);

// helpers
export const fmtEUR = (n) =>
  (Number(n) || 0).toLocaleString("el-GR", {
    style: "currency",
    currency: "EUR",
  });

/**
 * Feb 2026 — save export/backup to the FText Companion agent.
 *
 * The Ponte Pos POS PC runs in Chrome kiosk mode, where browser
 * downloads are silently blocked. Instead of triggering an `<a
 * download>` click, we:
 *   1. Fetch the file via axios (Bearer-authenticated) as a Blob.
 *   2. base64-encode the bytes in the browser.
 *   3. POST them to http://127.0.0.1:9100/save-file so the local
 *      companion writes to C:\\FText\\<folder>\\<filename>.
 *
 * Behaviour when the companion is unavailable is controlled by the
 * caller — most Ponte Pos exports use `fallback=false` and surface a
 * clear «Companion agent not reachable» toast (operator preference,
 * Feb 2026 handoff).
 *
 * @param path      API path AFTER `/api`
 * @param params    axios query params
 * @param folder    "backup" | "reports"  (whitelisted by the companion)
 * @param filename  target filename inside the folder
 * @param opts      { fallback?: boolean, onProgress?: (evt) => void }
 */
export const downloadAuthenticated = async (
  path,
  params,
  filename,
  opts = {},
) => {
  const {
    folder = "reports",
    saveToCompanion = true,
    fallback = true,
  } = opts;
  const resp = await http.get(path, { params, responseType: "blob" });
  const blob = resp.data;

  if (saveToCompanion) {
    try {
      // base64-encode the blob
      const arrayBuf = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuf);
      let bin = "";
      // chunked to avoid "call stack exceeded" on very large backups
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(
          null,
          bytes.subarray(i, i + CHUNK),
        );
      }
      const b64 = btoa(bin);
      const compResp = await fetch("http://127.0.0.1:9100/save-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder, filename, content_b64: b64 }),
      });
      if (!compResp.ok) {
        throw new Error(`Companion HTTP ${compResp.status}`);
      }
      const j = await compResp.json();
      if (!j.ok) {
        throw new Error(j.error || "Companion save failed");
      }
      return { savedTo: j.path, viaCompanion: true };
    } catch (err) {
      if (!fallback) throw err;
      // fall through to browser download
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "export";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return { savedTo: filename, viaCompanion: false };
};

// ---- Έλεγχος αποθέματος (Ιούν 2026) ----
// Επιστρέφει { qty, cls } για το badge αποθέματος ή null όταν το είδος
// δεν έχει ενεργό track_stock. Χρώματα: πράσινο / πορτοκαλί (≤ όριο) / κόκκινο (≤0).
export const stockBadge = (p, threshold = 5) => {
  if (!p?.track_stock) return null;
  const s = Number(p.stock_qty ?? 0);
  const qty = Number.isInteger(s) ? s : Number(s.toFixed(2));
  if (s <= 0) return { qty, cls: "bg-red-600 text-white" };
  if (s <= threshold) return { qty, cls: "bg-orange-500 text-white" };
  return { qty, cls: "bg-emerald-600 text-white" };
};

export const warnZeroStock = (p) => {
  if (p?.track_stock && Number(p.stock_qty ?? 0) <= 0) {
    toast.warning(`Μηδενικό απόθεμα: ${p.description}`, { duration: 2500 });
  }
};

// Πολλαπλασιαστής barcode (Ιούν 2026): επιστρέφει την ποσότητα που
// αντιστοιχεί στο σκαναρισμένο barcode (π.χ. barcode εξάδας → 6).
export const barcodeQty = (p, code) => {
  const v = (code || "").trim();
  if (!v) return 1;
  if ((p.barcode1 || "").trim() === v) return Number(p.barcode1_qty) || 1;
  if ((p.barcode2 || "").trim() === v) return Number(p.barcode2_qty) || 1;
  if ((p.barcode3 || "").trim() === v) return Number(p.barcode3_qty) || 1;
  return 1;
};

export const VAT_RATES = [
  { code: 1, rate: 6, label: "ΦΠΑ 6%" },
  { code: 2, rate: 13, label: "ΦΠΑ 13%" },
  { code: 3, rate: 24, label: "ΦΠΑ 24%" },
  { code: 4, rate: 36, label: "ΦΠΑ 36%" },
  { code: 5, rate: 0, label: "ΦΠΑ 0%" },
];

/**
 * Device-local override for grid layouts. Tablets in different roles
 * (bar / waiter) can pin their own columns/rows without affecting the
 * global ``Settings`` document. Returns a numeric value or ``null`` when
 * no override is set for this device.
 */
export const getDeviceGrid = (key) => {
  try {
    const v = localStorage.getItem(`device-grid-${key}`);
    if (v === null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

export const setDeviceGrid = (key, value) => {
  try {
    if (value === null || value === undefined || value === "")
      localStorage.removeItem(`device-grid-${key}`);
    else localStorage.setItem(`device-grid-${key}`, String(value));
  } catch {
    /* ignore */
  }
};


export const vatLabel = (code) =>
  VAT_RATES.find((v) => v.code === code)?.label || "—";

export const RECEIPT_TYPE_LABELS = {
  receipt: "Απόδειξη Λιανικής",
  credit_receipt: "Τιμολόγιο",
  restaurant_order: "Παραγγελία Τραπεζιού",
  restaurant_close: "Κλείσιμο Τραπεζιού",
  restaurant_move: "Μεταφορά Τραπεζιού",
  restaurant_status: "Κατάσταση Τραπεζιού",
  zreport: "Αναφορά Z",
  xreport: "Αναφορά X",
  dreport: "Αναφορά Τμημάτων",
  status: "Κατάσταση Συσκευής",
  rest_print_report: "Ανοιχτά Τραπέζια",
  doc_cancel: "Ακύρωση Παραστατικού",
  doc_reprint: "Επανεκτύπωση Παραστατικού",
  security: "Ασφάλεια",
  prepayment: "Προκαταβολή",
  refund_prepayment: "Επιστροφή Προκαταβολής",
  prg_setdepartment: "Ρύθμιση Τμήματος",
  prg_setpayment: "Ρύθμιση Πληρωμής",
  non_fiscal_order: "Παραγγελία (μη φορολογική)",
  raw: "Raw Command",
};
